import { collectRefs } from '../utils/_webComponents';
import { getDataProperty } from '../utils/_domFunctions';

const RADIO_QUERY_SELECTOR = 'input[type="radio"].address-card-radio';

/**
 * TODO:
 * - handle registered address errors (partially done by o-checkout)
 */
class mAddressSelector extends HTMLDivElement {
    /**
     * @listens mAddressSelectorCard#savecardedition
     * @listens mAddressSelectorCard#closecardedition
     */
    constructor() {
        super();
        collectRefs.call(this);

        // Get and setup all radio inputs
        this.addressCards = [];
        const addressCards = [...this.querySelectorAll('[is="m-address-selector-card"]')];
        addressCards.forEach(this.setupAddressCard.bind(this));
        this.updateSelectionState();

        // Address cards display limit
        this.addressCardsShown = false;
        this.addressCardLimit = getDataProperty(this, 'cardLimit', 'number', 2);
        this.refs.showMoreAddressesBtn?.addEventListener('click', () => {
            this.toggleAddressCards(true);
            // Move focus on first previously hidden card
            const cards = this.querySelectorAll('[is="m-address-selector-card"]:not([data-mode="addition"])');
            cards[this.addressCardLimit]?.querySelector('a, button, input, select')?.focus();
        });
        // Will call updateShowMoreAddressesBtnState() and updateBorderState() for us:
        this.toggleAddressCards(false);

        // New address form and button
        this.refs.newAddressBtn?.addEventListener('click', this.showAddressCreationCard.bind(this));
        this.refs.addressCreationCard?.addEventListener('savecardedition', (event) => this.addNewAddressCard(event.detail.templates.addressSelectorCard, true));
        this.refs.addressCreationCard?.addEventListener('closecardedition', this.cancelNewAddress.bind(this));

        // Allow a single card to be in edition mode
        this.currentCardEditionOpened = null;
        this.addEventListener('opencardedition', (event) => {
            if (this.currentCardEditionOpened) {
                if (this.currentCardEditionOpened === this.refs.addressCreationCard) {
                    this.cancelNewAddress();
                } else {
                    this.currentCardEditionOpened.setSummaryView();
                }
            }
            this.currentCardEditionOpened = event.target;
        });
        this.addEventListener('closecardedition', (event) => {
            if (this.currentCardEditionOpened === event.target) {
                this.currentCardEditionOpened = null;
            }
        });

        // Handle remove card event
        this.addEventListener('removecard', (event) => this.removeAddressCard(event.detail.addressId));
    }

    /**
     * Setup a address card and their content
     * @param {HTMLDivElement} cardEl Address card element
     */
    setupAddressCard(cardEl) {
        const radioEl = cardEl.querySelector(RADIO_QUERY_SELECTOR);
        const { addressIdSelected } = this.dataset;
        radioEl.setAttribute(
            'name',
            this.addressCards[0]?.querySelector(RADIO_QUERY_SELECTOR)?.name || radioEl.name,
        );
        radioEl.toggleAttribute('checked', addressIdSelected && radioEl.value === addressIdSelected);
        radioEl.addEventListener('change', this.updateSelectionState.bind(this));
        this.addressCards.push(cardEl);
    }

    /**
     * General update of the component needed to be done after a radio button selection
     * @fires mAddressSelector#change
     */
    updateSelectionState() {
        this.addressCards.forEach((card) => {
            const radio = card.querySelector(RADIO_QUERY_SELECTOR);
            card.classList.toggle('selected', radio.checked);

            // Setup action buttons
            card.querySelectorAll('.address-card-action').forEach((btn) => {
                if (radio.checked) {
                    btn.removeAttribute('tabindex');
                } else {
                    btn.setAttribute('tabindex', -1);
                }
            });

            if (radio.checked) {
                const { addressId } = card.dataset;
                /**
                 * @event mAddressSelector#change
                 * @type {object}
                 * @property {object} detail Event detail property
                 * @property {HTMLElement} detail.card Address card element that was selected
                 * @property {string} detail.addressId Address ID that was selected
                 */
                this.dispatchEvent(new CustomEvent('change', { detail: { card, addressId } }));
            }
        });
    }

    /**
     * Update border display
     */
    updateBorderState() {
        const items = [...this.querySelectorAll('.address-selector-item')];
        items.forEach((item) => item.classList.remove('first-card-item', 'last-card-item'));

        const visibleItems = items.filter((item) => window.getComputedStyle(item).getPropertyValue('display') !== 'none');
        if (visibleItems.length > 0) {
            visibleItems[0].classList.add('first-card-item');
            visibleItems[visibleItems.length - 1].classList.add('last-card-item');
        }
    }

    /**
     * Update showMoreAddressesBtn button visibility
     */
    updateShowMoreAddressesBtnState() {
        if (!this.refs.showMoreAddressesBtn) {
            return;
        }

        if (
            this.addressCardsShown
            || this.querySelectorAll('[is="m-address-selector-card"]:not([data-mode="addition"])').length <= this.addressCardLimit
        ) {
            this.refs.showMoreAddressesBtn.style.display = 'none';
        } else {
            this.refs.showMoreAddressesBtn.style.removeProperty('display');
        }
        this.updateBorderState();
    }

    /**
     * Show/Hide all cards that have an index above the limit (this.addressCardLimit)
     * @param {boolean} [force] Show if set to true or hide if set to false,
     * indepentently from its current state
     */
    toggleAddressCards(force) {
        this.addressCardsShown = force ?? !this.addressCardsShown;

        // Update card visibility
        const cards = this.querySelectorAll('[is="m-address-selector-card"]:not([data-mode="addition"])');
        cards.forEach((card, index) => {
            if (!this.addressCardsShown && index >= this.addressCardLimit) {
                card.style.display = 'none';
            } else {
                card.style.removeProperty('display');
            }
        });

        // Update hidden card counter
        const hiddenCardsCount = cards.length - this.addressCardLimit;
        const showMoreCountEl = this.refs.showMoreCount;
        if (showMoreCountEl && hiddenCardsCount > 0) {
            showMoreCountEl.textContent = `(${hiddenCardsCount})`;
        }

        this.updateShowMoreAddressesBtnState();
    }

    /**
     * Show address creation card
     */
    showAddressCreationCard() {
        this.refs.addressCreationCard.setEditionView();
        this.refs.addressCreationCard.style.removeProperty('display');
        if (this.refs.newAddressBtn) {
            this.refs.newAddressBtn.style.display = 'none';
            if (document.activeElement === this.refs.newAddressBtn) {
                this.refs.addressCreationCard.querySelector('address-card-edition')?.focus();
            }
        }
        this.updateBorderState();
    }

    /**
     * Select address card corresponding to an address ID
     * @param {[string]} addressId Address ID of the card to select.
     * Will unselect all if ID doesn't exists.
     */
    selectAddressCard(addressId) {
        this.querySelectorAll(RADIO_QUERY_SELECTOR).forEach((radioEl) => {
            if (addressId && radioEl.value === addressId) {
                radioEl.toggleAttribute('checked', true);
                radioEl.checked = true;
                this.dataset.addressIdSelected = addressId;
            } else {
                radioEl.toggleAttribute('checked', false);
                radioEl.checked = false;
            }
        });
        this.updateSelectionState();
    }

    /**
     * Add an new address in the address card list
     * This method is called externally
     * @param {string} newCardHTML HTML string containing an address card
     * @param {boolean} checked If checked, the card will be selected after creation
     */
    addNewAddressCard(newCardHTML, checked) {
        // Create and setup address card from HTML template string
        const tmpEl = document.createElement('div');
        tmpEl.innerHTML = newCardHTML;
        const newCard = tmpEl.children[0];
        const radioEl = newCard.querySelector(RADIO_QUERY_SELECTOR);
        this.setupAddressCard(newCard);

        // Attach created card 
        // before creation card (will be last in the list)
        this.refs.addressCreationCard.before(newCard);

        // Focus on address card radio if user was focus on the new address form
        if (
            document.activeElement
            && document.activeElement.contains(this.refs.addressCreationCard)
        ) {
            newCard?.focus();
            newCard?.scrollIntoView({ behavior: "smooth", block: 'center' });
        }

        // Redisplay "create new address" button
        if (this.refs.newAddressBtn) {
            this.refs.newAddressBtn.style.removeProperty('display');
        }

        // Hide new address card form
        this.refs.addressCreationCard.style.display = 'none';

        // Select new address card
        if (checked) {
            this.selectAddressCard(radioEl?.value);
        }

        this.updateBorderState();
    }

    /**
     * Cancel new address creation and close the form
     */
    cancelNewAddress() {
        // Redisplay "create new address" button and focus on it
        if (this.refs.newAddressBtn) {
            this.refs.newAddressBtn.style.removeProperty('display');
            if (
                document.activeElement
                && this.refs.addressCreationCard.contains(document.activeElement)
            ) {
                this.refs.newAddressBtn.focus();
                this.refs.newAddressBtn.scrollIntoView({ block: 'center' });
            }
        }

        // Hide new address card form
        this.refs.addressCreationCard.style.display = 'none';

        this.updateBorderState();
    }

    /**
     * Update an address card with new data
     * This method is called externally
     * @param {string} oldAddressId Previous address ID from the card that need modification
     * @param {string} newAddressId New address ID
     * @param {string} summaryHTML New address card Summary as HTML string
     */
    updateAddressCard(oldAddressId, newAddressId, summaryHTML) {
        const card = this.addressCards.find((c) => c.dataset.addressId === oldAddressId);
        if (!card) {
            return;
        }
        card.updateAddressCard(newAddressId, summaryHTML);
    }

    /**
     * Remove an address card
     * @param {string} addressId Address ID from the card to remove
     */
    removeAddressCard(addressId) {
        const cardIdx = this.addressCards.findIndex((c) => c.dataset.addressId === addressId);
        if (cardIdx === -1) {
            return;
        }
        const card = this.addressCards[cardIdx];
        this.addressCards.splice(cardIdx, 1);
        card.remove();

        // Handle case when the deleted address was selected
        const radioEl = card.querySelector(RADIO_QUERY_SELECTOR);
        if (radioEl?.checked && this.addressCards.length > 0) {
            const newRadioEl = this.addressCards[0].querySelector(RADIO_QUERY_SELECTOR);
            this.selectAddressCard(newRadioEl.value);
        }

        this.updateBorderState();
    }
}

customElements.define('m-address-selector', mAddressSelector, {
    extends: 'div',
});
